import React, { useCallback, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import AppsIcon from '@atlaskit/icon/core/apps';
import { Box, Flex, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { AppItems } from '../../../common/ui/more-apps/apps-group/apps-group-item/types';
import { CustomizeButtons } from '../../../controllers/customize-buttons';
import { useAppItemsState } from '../../../controllers/use-app-items-state';

import { AppsGroup } from './apps-group';
import { MAX_SELECTED_APPS, MORE_APPS_CONTAINER_WIDTH } from './constants';
import { ErrorState } from './empty-states/error-state';
import { NoResultsState } from './empty-states/no-results-state';
import { FilterApps } from './filter-apps';
import { messages } from './messages';
import type { MoreAppsProps } from './types';

const MORE_APPS_MAX_HEIGHT = '99vh';

const styles = cssMap({
	container: {
		display: 'flex',
		flexDirection: 'column',
		height: MORE_APPS_MAX_HEIGHT,
		maxHeight: MORE_APPS_MAX_HEIGHT,
		overflow: 'hidden',

		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),

		borderWidth: token('border.width'),
		borderRadius: token('radius.large'),
		borderColor: token('color.border'),
		borderStyle: 'solid',
		boxShadow: token('elevation.shadow.overlay'),
	},
	stack: {
		flex: 1,
		minHeight: '0px',
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
	},
	headerRow: {
		flexShrink: 0,
	},
	filterRow: {
		flexShrink: 0,
	},
	moreAppsContainer: {
		flex: 1,
		minHeight: '0px',
		overflowY: 'auto',
		overflowX: 'hidden',
		width: '100%',
		flexDirection: 'column',
		paddingBottom: token('space.150'),
	},
	exploreMoreAppsButtonContainer: {
		flexShrink: 0,
		width: '100%',
		borderTopWidth: token('border.width'),
		borderTopColor: token('color.border'),
		borderTopStyle: 'solid',
		paddingTop: token('space.100'),
	},
});

/**
 * PLEASE NOTE:
 * This is an INCOMPLETE early implementation of the More Apps component that could be used in the ribbon
 * for accessing additional applications.
 *
 * This will be completed by https://hello.jira.atlassian.cloud/browse/NAVX-3348
 */
export const MoreApps = (props: MoreAppsProps): React.JSX.Element => {
	const {
		appItems = [],
		atlassianAppsItems = [],
		integrationsAppsItems = [],
		marketplaceAppsItems = [],
		hasLoadError = false,
		maxSelectedApps = MAX_SELECTED_APPS,
		onUpdateItems,
		containerWidth = MORE_APPS_CONTAINER_WIDTH,
	} = props;
	const intl = useIntl();

	const [isCustomizing, setIsCustomizing] = useState(false);
	const allAppItems = useMemo<AppItems>(() => {
		return {
			appItems,
			atlassianAppsItems,
			integrationsAppsItems,
			marketplaceAppsItems,
		};
	}, [appItems, atlassianAppsItems, integrationsAppsItems, marketplaceAppsItems]);
	const [filteredAppItems, setFilteredAppItems] = useState<AppItems>(allAppItems);

	const { processedItems, selectedApps, onToggleSelect } = useAppItemsState({
		items: allAppItems,
		filteredItems: filteredAppItems,
		setFilteredAppItems,
		isCustomizing,
		maxSelectedApps,
	});

	const totalProcessedItemsCount =
		processedItems.appItems.length +
		processedItems.atlassianAppsItems.length +
		processedItems.integrationsAppsItems.length +
		processedItems.marketplaceAppsItems.length;

	const showNoResultsState = totalProcessedItemsCount === 0;

	const onClickCancelButton = useCallback(() => {
		// TODO Analytics
	}, []);

	// @TODO Apply save functionality
	const onClickApplyButton = useCallback(() => {
		// TODO Analytics
		onUpdateItems?.(processedItems);
	}, [processedItems, onUpdateItems]);

	if (hasLoadError) {
		return <ErrorState />;
	}

	return (
		<Box xcss={styles.container} style={{ width: containerWidth, maxWidth: containerWidth }}>
			<Stack space="space.150" xcss={styles.stack}>
				<Inline xcss={styles.headerRow} spread="space-between" alignBlock="center">
					<Text color="color.text.accent.gray" weight="bold">
						{intl.formatMessage(messages.moreAppsLabel)}
					</Text>
					<Inline space="space.050">
						<CustomizeButtons
							isCustomizing={isCustomizing}
							setIsCustomizing={setIsCustomizing}
							onCancel={onClickCancelButton}
							onApply={onClickApplyButton}
						/>
					</Inline>
				</Inline>
				<Flex xcss={styles.filterRow}>
					<FilterApps appItems={allAppItems} onChange={setFilteredAppItems} />
				</Flex>
				<Flex xcss={styles.moreAppsContainer}>
					{showNoResultsState ? (
						<NoResultsState />
					) : (
						<>
							{isCustomizing && (
								<AppsGroup
									title={intl.formatMessage(messages.appsSelectedLabel, {
										count: selectedApps.size,
										max: maxSelectedApps,
									})}
									isCustomizing={isCustomizing}
									items={processedItems.appItems}
									onToggleSelect={onToggleSelect}
								/>
							)}
							<AppsGroup
								isCustomizing={isCustomizing}
								title={intl.formatMessage(messages.atlassianAppsGroupTitle)}
								items={processedItems.atlassianAppsItems}
								onToggleSelect={onToggleSelect}
							/>
							<AppsGroup
								isCustomizing={isCustomizing}
								title={intl.formatMessage(messages.marketplaceAppsGroupTitle)}
								items={processedItems.marketplaceAppsItems}
								onToggleSelect={onToggleSelect}
							/>
							<AppsGroup
								isCustomizing={isCustomizing}
								title={intl.formatMessage(messages.integrationsAppsGroupTitle)}
								items={processedItems.integrationsAppsItems}
								onToggleSelect={onToggleSelect}
							/>
						</>
					)}
					{/* Selected apps should only be displayed when customizing */}
				</Flex>
				<Box xcss={styles.exploreMoreAppsButtonContainer}>
					<Button appearance="default" iconBefore={AppsIcon} shouldFitContainer>
						{intl.formatMessage(messages.exploreMoreAppsButtonLabel)}
					</Button>
				</Box>
			</Stack>
		</Box>
	);
};
